import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { APIClient } from "../../helpers/api_helper";
import * as url from "../../helpers/url_helper";

const api = new APIClient();

/**
 * Fetch user profile data from backend
 * Extracts user ID from JWT and retrieves full profile
 */
export const fetchUserProfile = createAsyncThunk(
  "profile/fetchUserProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(url.GET_USER_PROFILE);
      if (!response || Object.keys(response).length === 0) {
        return rejectWithValue("No profile data returned from server");
      }
      return response;
    } catch (error) {
      const errorMessage = typeof error === "string" ? error : error?.message || "Failed to fetch profile";
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Update user profile with new information
 * Validates and updates all profile fields
 */
export const updateUserProfile = createAsyncThunk(
  "profile/updateUserProfile",
  async (profileData, { rejectWithValue }) => {
    try {
      if (!profileData || Object.keys(profileData).length === 0) {
        return rejectWithValue("No profile data provided");
      }

      const response = await api.put(url.UPDATE_USER_PROFILE, profileData);

      if (!response || !response.message) {
        return rejectWithValue("Invalid response from server");
      }

      return response;
    } catch (error) {
      const errorMessage = typeof error === "string" ? error : error?.message || "Failed to update profile";
      return rejectWithValue(errorMessage);
    }
  }
);

const initialState = {
  profile: null,
  loading: false,
  error: null,
  successMessage: null,
};

const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    /**
     * Reset error and success messages
     */
    resetProfileState: (state) => {
      state.successMessage = null;
      state.error = null;
    },
    /**
     * Clear all profile data
     */
    clearProfileData: (state) => {
      state.profile = null;
      state.successMessage = null;
      state.error = null;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    // Fetch profile cases
    builder
      .addCase(fetchUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
        state.error = null;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch profile. Please try again.";
        state.profile = null;
      })

      // Update profile cases
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message || "Profile updated successfully";
        state.error = null;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update profile. Please try again.";
        state.successMessage = null;
      });
  },
});

export const { resetProfileState, clearProfileData } = profileSlice.actions;
export default profileSlice.reducer;
